import { useEffect, useState } from "react";
import { Download, Share } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export function useInstallable() {
  const [prompt, setPrompt] = useState(() => window.__installPrompt || null);
  const [installed, setInstalled] = useState(isStandalone);
  useEffect(() => {
    const onReady = () => setPrompt(window.__installPrompt);
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("grh-installable", onReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("grh-installable", onReady); window.removeEventListener("appinstalled", onInstalled); };
  }, []);
  const install = async () => {
    if (!prompt) return false;
    prompt.prompt();
    await prompt.userChoice.catch(() => null);
    window.__installPrompt = null;
    setPrompt(null);
    return true;
  };
  return { canInstall: !installed && (!!prompt || isIOS()), install, needsIOSHint: !prompt && isIOS() };
}

export function InstallAppButton({ className, style, label = "Install app", children, testId = "install-app-btn" }) {
  const { canInstall, install, needsIOSHint } = useInstallable();
  const [hint, setHint] = useState(false);
  if (!canInstall) return null;
  const onClick = () => (needsIOSHint ? setHint(true) : install());
  return (
    <>
      <button onClick={onClick} data-testid={testId} className={className} style={style}>
        {children || <><Download className="w-3.5 h-3.5" /> {label}</>}
      </button>
      <Dialog open={hint} onOpenChange={setHint}>
        <DialogContent className="max-w-sm" data-testid="ios-install-hint">
          <DialogHeader><DialogTitle className="font-serif text-xl">Add to Home Screen</DialogTitle></DialogHeader>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal pl-5">
            <li>Tap the <Share className="w-4 h-4 inline -mt-1" /> Share button in Safari.</li>
            <li>Scroll down and choose <strong>Add to Home Screen</strong>.</li>
            <li>Tap <strong>Add</strong> — the app will appear on your home screen.</li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
